/**
 * Downloads the employee entitlements register as the FEG verification
 * spreadsheet.
 *
 * Excel only — the FEG Branch DB takes a spreadsheet upload, so the
 * Word / PDF options the other agent exports offer would be misleading
 * here.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { saveAs } from "file-saver";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { buildFegWorkbook, countUnexportableBasis } from "./buildFegWorkbook";
import type { EmployeeEntryUI } from "./types";

/** The job fields the export needs. */
export interface FegExportJob {
  orgId?: Id<"organizations">;
  entityName?: string;
  abn?: string;
  administrationDate?: string;
  administrationStartDate?: string;
  liquidationDate?: string;
  administrationContinuous?: boolean;
  appointmentDate?: string;
}

function sanitiseFilename(name: string): string {
  return name.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
}

export function FegExportButton({
  entries,
  job,
}: {
  /** Active employee entries. Merged rows should already be filtered out. */
  entries: EmployeeEntryUI[];
  job: FegExportJob | null;
}) {
  const [busy, setBusy] = useState(false);

  // FEG asks for the name of the person entering the data.
  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );

  // The practitioner contact line comes from the firm letterhead rather
  // than new schema fields — it is the same phone and email the firm
  // already puts on outgoing correspondence.
  const { data: branding } = useQuery({
    ...convexQuery(api.settings.branding.queries.getBrandingProfile, {
      orgId: job?.orgId as Id<"organizations">,
    }),
    enabled: !!job?.orgId,
  });

  const handleExport = () => {
    if (entries.length === 0) {
      toast.error("There are no employees to export.");
      return;
    }

    setBusy(true);
    try {
      const completedBy = [currentUser?.firstName, currentUser?.lastName]
        .filter(Boolean)
        .join(" ");

      const blob = buildFegWorkbook(
        entries,
        {
          administrationDate:
            job?.administrationStartDate ?? job?.administrationDate,
          liquidationDate: job?.liquidationDate,
          administrationContinuous: job?.administrationContinuous,
          appointmentDate: job?.appointmentDate,
        },
        {
          employerLegalName: job?.entityName,
          employerAbn: job?.abn,
          completedBy: completedBy || undefined,
          administrationDate:
            job?.administrationStartDate ?? job?.administrationDate,
          liquidationDate: job?.liquidationDate,
          administrationContinuous: job?.administrationContinuous,
          contactPhone: branding?.headerPhone,
          contactEmail: branding?.headerEmail,
        },
      );

      const suffix = job?.entityName
        ? `_${sanitiseFilename(job.entityName)}`
        : "";
      saveAs(blob, `FEG_Entitlement_Spreadsheet${suffix}.xlsx`);

      // FEG accepts only Full-time / Part-time / Casual, so any other
      // basis exports blank. Say so rather than let it pass unnoticed.
      const unexportable = countUnexportableBasis(entries);
      if (unexportable > 0) {
        toast.warning(
          `${unexportable} ${unexportable === 1 ? "employee has a basis" : "employees have a basis"} of employment FEG doesn't accept, so that column was left blank. FEG only accepts Full-time (permanent), Part-time (permanent) or Casual.`,
        );
      }
    } catch (err) {
      console.error("FEG export failed:", err);
      toast.error("Couldn't build the FEG spreadsheet. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5"
      disabled={busy || entries.length === 0}
      onClick={handleExport}
      title="Download the register as the Fair Entitlements Guarantee verification spreadsheet"
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <FileSpreadsheet className="h-3.5 w-3.5" />
      )}
      FEG Export
    </Button>
  );
}
